/* eslint-disable @typescript-eslint/no-explicit-any */
import slugify from 'slugify';
import logging from './logging';

/**
 * translateProperty
 * @param data Component object to have property translated
 * @param property property to be translated
 * @param dynamicField Dynamic field name from data object
 * @returns
 */
export const translateProperty = (
  data: Record<string, unknown>,
  properties: string | string[],
  dynamicField: string,
) => {
  let translatedObj = data;
  for (const property of properties) {
    const { [dynamicField]: dynamic, ...service } = translatedObj;
    const obj = (
      dynamic as {
        [key: string]: { [key: string]: string };
      }[]
    )?.map((item) => {
      const name = `${property}${item.__typename}`;
      const value = item[name];
      if (value !== undefined) {
        const { [name]: _, ...obj } = item;
        return { [property]: value, ...obj };
      } else return item;
    });
    translatedObj = { [dynamicField]: obj, ...service };
  }

  return translatedObj;
};

export type Data = {
  id?: string;
  documentId?: string;
  attributes?: Record<string, unknown>;
};

export type FlattenArg = { [key: string]: unknown };

/**
 * Remove first level of nesting from Strapi queries response.
 * Supports both Strapi v4 (data.attributes) and Strapi v5 (flat fields) formats.
 * @param data object data to remove nesting
 * @returns new object
 */
export const flatten = (data: FlattenArg) => {
  return Object.keys(data).reduce((acc, key) => {
    const value = data[key];

    // Strapi v5: value is already an array of flat objects
    if (Array.isArray(value)) {
      const firstEntry = value[0] as Data | undefined;
      if (firstEntry && firstEntry.attributes) {
        // v4 array: strip data.attributes wrapper
        return {
          ...acc,
          [key]: value.map((entry: Data) => ({
            ...(entry?.id && { id: entry.id }),
            ...entry.attributes,
          })),
        };
      }
      // v5 array: already flat
      return { ...acc, [key]: value };
    }

    // Strapi v4: value is { data: Data[] | Data }
    const v4Value = value as { data?: Data[] | Data };
    if (v4Value && typeof v4Value === 'object' && 'data' in v4Value) {
      const dataField = v4Value.data;
      if (Array.isArray(dataField)) {
        return {
          ...acc,
          [key]: dataField.map((entry: Data) => {
            if (entry.attributes)
              return {
                ...(entry?.id && { id: entry.id }),
                ...entry.attributes,
              };
            return entry;
          }),
        };
      }
      return {
        ...acc,
        [key]: {
          ...((dataField as Data)?.id && { id: (dataField as Data)?.id }),
          ...(dataField as Data)?.attributes,
        },
      };
    }

    // Strapi v5: value is a single flat object
    return { ...acc, [key]: value };
  }, {}) as Record<string, unknown>;
};

export const deepFlatten = (data: Data[]) =>
  data.map((d) => ({ ...(d.id && { id: d.id }), ...d.attributes }));

export const isLookingForJob = (message?: string) => {
  if (!message) return false;

  const whiteList = [
    'vaga',
    'estagio',
    'oportunidade',
    'emprego',
    'curriculo',
    'cv',
    'seletivo',
    'estagiario',
    'estagiarios',
    'estagiar',
    'aprendiz',
    'trabalho',
    'trabalhar',
    'cursando',
    'junior',
    'seletivo',
    'trainee',
  ];

  const whiteList2 = [
    'oportunidade',
    'estagio',
    'emprego',
    'curriculo',
    'experiencia',
    'vaga',
    'estagiario',
    'estagiarios',
    'estagiar',
    'aprendiz',
    'faculdade',
    'trabalho',
    'trabalhar',
    'estudando',
    'busca',
    'começando',
    'cursando',
    'junior',
  ];

  // whiteList words can't appear
  if (
    whiteList.filter(
      (word) => slugify(message, { replacement: ' ', lower: true }).indexOf(word) > -1,
    )?.length > 0
  ) {
    return true;
  }

  // whiteList2 words can appear only once
  if (
    whiteList2.filter(
      (word) => slugify(message, { replacement: ' ', lower: true }).indexOf(word) > -1,
    )?.length > 1
  )
    return true;
  return false;
};

// All dynamic zone components that can be embedded in richtext
// Add component names WITHOUT the "ComponentComponents" prefix
// These components can be referenced in richtext using tags like [ComponentName] or [ComponentName id="..."]
const componentsWhiteList = [
  'AvailableJobs',
  'Banner',
  'Blog',
  'BudgetForm',
  'CompanyForm',
  'ContactCard',
  'ContactForm',
  'DevelopmentDetails',
  'DottedList',
  'EventList',
  'Form',
  'Gallery',
  'GetInTouch',
  'Glassdoor',
  'HubspotMeeting',
  'ProjectsList',
  'Section',
  'SectionBackgroundImageRichText',
  'SectionCards',
  'SectionImageRichText',
  'SectionImageRichTextList',
  'SectionQuote',
  'SectionTextButtons',
  'SectionTextColumns',
  'SectionTitleItemList',
  'TeamCalculator',
  'Technologies',
  'TestimonialDetail',
  'TextWithSideList',
  'Timeline',
  'UserTestimony',
];

export const getRegex = (whiteList?: string[]) =>
  new RegExp(
    `((?:${
      whiteList ||
      componentsWhiteList.reduce(
        (acc, curr, i) => acc + `\\[${curr}${i === componentsWhiteList.length - 1 ? '' : '|'}`,
        '',
      )
    })[^\\]]*(?:\\]))`,
    'g',
  );

const replaceArray = (str: string) => {
  // Remove component names from the string
  for (const component of componentsWhiteList) {
    str = str.replace(component, '');
  }

  // Return the remaining text after component name
  // Example: "Gallery Precos" -> "Precos"
  return str.trim();
};

const componentShouldBeFound = (id: string) => {
  if (componentsWhiteList.find((component) => id.includes(component))) {
    return true;
  }
  return false;
};

/**
 * Orders dynamic zone components based on their placement in rich text
 *
 * This function processes rich text content and replaces component tags with actual components.
 * It supports two modes of component insertion:
 *
 * 1. **ID-based (legacy)**: Components with `richTextId` property
 *    - Usage in rich text: `[Gallery my-gallery-id]` or `[SectionQuote quote-1]`
 *    - Matches components by their `richTextId` property
 *    - Example: A Gallery component with `richTextId: "my-gallery-id"` will replace `[Gallery my-gallery-id]`
 *
 * 2. **Index-based (new)**: Components without `richTextId` property
 *    - Usage in rich text: `[SectionTextButtons]` or `[Gallery]`
 *    - Matches components by order of appearance (first occurrence matches first component, etc.)
 *    - Example: If you have 2 SectionTextButtons in the dynamic zone and 2 `[SectionTextButtons]` tags
 *      in your rich text, the first tag gets the first component, the second tag gets the second component
 *
 * @param dynamicZone - Array of dynamic zone components from Strapi
 * @returns Reordered array with rich text components expanded
 *
 * @example
 * // Rich text content: "Some text [Gallery gallery-1] more text [SectionTextButtons]"
 * // Dynamic zone: [
 * //   { __typename: 'ComponentComponentsRichtext', body: '...' },
 * //   { __typename: 'ComponentComponentsGallery', richTextId: 'gallery-1', ... },
 * //   { __typename: 'ComponentComponentsSectionTextButtons', ... } // no richTextId
 * // ]
 * // Result: The Gallery will be inserted by ID, SectionTextButtons by index (first one found)
 */
export const orderDataByRichTextId = (dynamicZone: any[], context: any) => {
  let newDz = [] as any[];
  // Track used components by type to manage index-based replacement
  const componentUsageCount: Record<string, number> = {};
  // Track which components were actually used
  const usedComponentIds = new Set<number>();

  for (const dz of dynamicZone) {
    if (dz.__typename === 'ComponentComponentsRichtext') {
      const regex = getRegex();
      const components = dz.body.split(regex);

      const mappedComponents = components.map((component: string) => {
        // Extract component type from the tag (e.g., [Gallery id] or [SectionTextButtons])
        const cleanComponent = component.replace('[', '').replace(']', '').trim();

        // Try to find exact match first (e.g., "SectionTextButtons")
        // or match with ID (e.g., "Gallery Precos" -> "Gallery")
        let componentType = componentsWhiteList.find((type) => cleanComponent === type);

        // If no exact match, try to find by checking if it starts with component name + space
        // This handles cases like "Gallery Precos" where "Gallery" is the component
        if (!componentType) {
          componentType = componentsWhiteList.find((type) => {
            // Check if cleanComponent starts with "ComponentName " (with space)
            if (cleanComponent.startsWith(type + ' ')) {
              return true;
            }
            return false;
          });
        }

        if (!componentType) {
          // Not a dynamic component, just return the text
          return {
            ...dz,
            body: component,
          };
        }

        // Extract the ID part (everything after the component type)
        const idPart = replaceArray(cleanComponent);

        // Try to find by richTextId first (legacy behavior)
        const foundByIdComponentIndex = dynamicZone.findIndex(
          (dzAux) => dzAux.richTextId && idPart === dzAux.richTextId,
        );

        if (foundByIdComponentIndex !== -1) {
          const foundByIdComponent = dynamicZone[foundByIdComponentIndex];
          usedComponentIds.add(foundByIdComponentIndex);
          return foundByIdComponent;
        }

        // If not found by ID, try to find by index (new behavior)
        // Get all components of this type that don't have richTextId
        const componentsOfTypeWithIndices = dynamicZone
          .map((dzAux, idx) => ({ component: dzAux, originalIndex: idx }))
          .filter(
            ({ component }) =>
              component.__typename === `ComponentComponents${componentType}` &&
              !component.richTextId,
          );

        if (componentsOfTypeWithIndices.length > 0) {
          // Initialize counter for this component type if not exists
          if (componentUsageCount[componentType] === undefined) {
            componentUsageCount[componentType] = 0;
          }

          const currentIndex = componentUsageCount[componentType];

          // Check if we have a component at this index
          if (currentIndex < componentsOfTypeWithIndices.length) {
            const { component: foundComponent, originalIndex } =
              componentsOfTypeWithIndices[currentIndex];
            usedComponentIds.add(originalIndex);
            componentUsageCount[componentType]++;
            return foundComponent;
          }
        }

        // Component not found - log error and return text
        if (componentShouldBeFound(component)) {
          logging.error(
            `Component ${componentType} not found. Tag: ${component}, Index: ${
              componentUsageCount[componentType] || 0
            }`,
            context,
          );
        }

        return {
          ...dz,
          body: component,
        };
      });
      newDz = newDz.concat(mappedComponents);
      continue;
    }

    // Get the current index in the original array
    const currentIndex = dynamicZone.indexOf(dz);

    // Skip components that have richTextId (they will be embedded in richtext)
    if (dz.richTextId) {
      continue;
    }

    // Check if this component was already used in richtext replacement
    if (usedComponentIds.has(currentIndex)) {
      continue;
    }

    // Keep standalone components in their original position. Components that were embedded in
    // rich text have already been skipped by `usedComponentIds` above.
    newDz.push(dz);
  }

  return newDz;
};

export const LOOKING_FOR_JOB_KEY = 'lookingForJob';
